import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Download, Paperclip } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Select } from '@/shared/components/ui/Select';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { ActivityTimeline } from '@/features/activity/components/ActivityTimeline';
import { defaultEntityActivities } from '@/features/activity/adapters/entityActivity';
import type { UserOption } from '@/shared/hooks/useUserOptions';
import { CommentThread } from './CommentThread';
import { useDemo, useDemoMutations } from '../hooks/useDemos';
import {
  DEMO_DELETE_ENABLED,
  DEMO_PRIORITY_OPTIONS,
  DEMO_PRIORITY_TONES,
  DEMO_STATUS_LABELS,
  DEMO_STATUS_OPTIONS,
  DEMO_STATUS_TONES,
  FEEDBACK_STATUS_OPTIONS,
} from '../constants';
import { getAssignedId } from '../types';
import type { DemoPriority, DemoStatus } from '../types';

interface DemoDrawerProps {
  demoId: string | null;
  currentUserId?: string;
  privileged: boolean;
  canDelete: boolean;
  users: UserOption[];
  onClose: () => void;
}

const ATT_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 border-b border-outline py-2 last:border-0">
      <span className="text-body-sm text-on-surface-variant">{label}</span>
      <span className="text-right text-data-md text-on-surface">{value}</span>
    </div>
  );
}

export function DemoDrawer({ demoId, currentUserId, privileged, canDelete, users, onClose }: DemoDrawerProps) {
  const { data, isLoading } = useDemo(demoId);
  const { updateDemo, assignDemo, addComment, addFeedback, deleteDemo } = useDemoMutations();
  const [confirmDel, setConfirmDel] = useState(false);
  const [fbRating, setFbRating] = useState('5');
  const [fbStatus, setFbStatus] = useState('accepted');
  const [fbText, setFbText] = useState('');

  const demo = data?.demo;
  const ownerId =
    typeof demo?.requested_by === 'string' ? demo?.requested_by : demo?.requested_by?._id ?? demo?.requested_by?.id;
  const canEdit = privileged || (!!currentUserId && ownerId === currentUserId);

  const timelineItems = defaultEntityActivities(data?.activities);

  const save = (payload: Parameters<typeof updateDemo.mutate>[0]['payload'], id: string) =>
    updateDemo.mutate(
      { id, payload },
      { onSuccess: () => toast.success('Demo updated.'), onError: () => toast.error('Update failed.') },
    );

  const assigneeOptions = [{ value: '', label: '— Unassigned —' }, ...users.map((u) => ({ value: u.id, label: u.display_name }))];

  return (
    <Drawer open={!!demoId} onClose={onClose} title={demo ? `${demo.demo_code ? demo.demo_code + ' · ' : ''}${demo.title}` : 'Demo'} width="w-[560px]">
      {isLoading || !demo ? (
        <div className="flex h-32 items-center justify-center"><Spinner /></div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={DEMO_STATUS_TONES[demo.status] ?? 'neutral'}>{DEMO_STATUS_LABELS[demo.status] ?? demo.status}</Badge>
            {demo.priority && <Badge tone={DEMO_PRIORITY_TONES[demo.priority] ?? 'neutral'}>{demo.priority}</Badge>}
            {typeof demo.progress === 'number' && <Badge tone="info">{demo.progress}%</Badge>}
          </div>

          {/* Lifecycle controls (owner or privileged) */}
          {canEdit && (
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Status"
                name="demo-status"
                value={demo.status}
                onChange={(e) => save({ status: e.target.value as DemoStatus }, demo.id)}
                options={DEMO_STATUS_OPTIONS}
              />
              <Select
                label="Priority"
                name="demo-priority-edit"
                value={demo.priority ?? 'medium'}
                onChange={(e) => save({ priority: e.target.value as DemoPriority }, demo.id)}
                options={DEMO_PRIORITY_OPTIONS}
              />
              <Input
                label="Progress (%)"
                name="demo-progress"
                type="number"
                min={0}
                max={100}
                defaultValue={demo.progress ?? 0}
                onBlur={(e) => {
                  const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                  if (v !== (demo.progress ?? 0)) save({ progress: v }, demo.id);
                }}
              />
              {privileged && (
                <Select
                  label="Assigned To"
                  name="demo-assign"
                  value={getAssignedId(demo.assigned_to)}
                  onChange={(e) =>
                    assignDemo.mutate(
                      { id: demo.id, assigned_to: e.target.value || null },
                      { onSuccess: () => toast.success('Assignment updated.'), onError: () => toast.error('Assign failed.') },
                    )
                  }
                  options={assigneeOptions}
                />
              )}
            </div>
          )}

          {/* Details */}
          <div>
            <Row label="Project Type" value={demo.project_type} />
            <Row label="Client" value={demo.client?.name} />
            <Row label="Company" value={demo.client?.company} />
            <Row label="Contact" value={demo.client?.mobile || demo.client?.email} />
            <Row label="Requested By" value={demo.requester_name} />
            <Row label="Assignee" value={demo.assignee_name} />
            <Row label="Expected Delivery" value={demo.expected_delivery_date?.slice(0, 10)} />
          </div>

          {demo.requirements && (
            <div>
              <h3 className="mb-1 text-label-caps uppercase text-on-surface-variant">Requirements</h3>
              <p className="whitespace-pre-wrap text-data-md text-on-surface">{demo.requirements}</p>
            </div>
          )}

          {/* Attachments */}
          {(demo.attachments?.length ?? 0) > 0 && (
            <div>
              <h3 className="mb-2 text-label-caps uppercase text-on-surface-variant">Attachments</h3>
              <div className="space-y-1">
                {demo.attachments!.map((a) => (
                  <a
                    key={a.file}
                    href={`${ATT_BASE}/attachments/${a.file}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between rounded-DEFAULT border border-outline bg-surface-variant px-3 py-2 text-data-md text-on-surface hover:bg-surface"
                  >
                    <span className="flex min-w-0 items-center gap-2"><Paperclip className="h-4 w-4 shrink-0" /><span className="truncate">{a.name}</span></span>
                    <Download className="h-4 w-4 shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div>
            <h3 className="mb-2 text-label-caps uppercase text-on-surface-variant">Timeline</h3>
            <ActivityTimeline items={timelineItems} showActor emptyTitle="No activity yet" emptyDescription="Lifecycle events will appear here." />
          </div>

          {/* Comments */}
          <div>
            <h3 className="mb-2 text-label-caps uppercase text-on-surface-variant">Comments</h3>
            <CommentThread
              comments={data?.comments ?? []}
              posting={addComment.isPending}
              onAdd={(text) => addComment.mutate({ id: demo.id, comment: text }, { onError: () => toast.error('Failed to comment.') })}
            />
          </div>

          {/* Feedback */}
          <div>
            <h3 className="mb-2 text-label-caps uppercase text-on-surface-variant">Feedback</h3>
            {(data?.feedback?.length ?? 0) > 0 && (
              <ul className="mb-3 flex flex-col gap-2">
                {data!.feedback.map((f) => (
                  <li key={f.id} className="rounded-card border border-outline bg-surface px-3 py-2">
                    <div className="flex items-center justify-between">
                      <Badge tone={f.status === 'rejected' ? 'danger' : f.status === 'revision_required' ? 'warning' : 'success'}>{f.status}</Badge>
                      {typeof f.rating === 'number' && f.rating > 0 && <span className="text-body-sm text-on-surface-variant">★ {f.rating}</span>}
                    </div>
                    {f.feedback && <p className="mt-1 text-body-sm text-on-surface-variant">{f.feedback}</p>}
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Select label="Outcome" name="fb-status" value={fbStatus} onChange={(e) => setFbStatus(e.target.value)} options={FEEDBACK_STATUS_OPTIONS} />
              <Input label="Rating (1–5)" name="fb-rating" type="number" min={1} max={5} value={fbRating} onChange={(e) => setFbRating(e.target.value)} />
            </div>
            <Input label="Feedback" name="fb-text" value={fbText} onChange={(e) => setFbText(e.target.value)} placeholder="Optional notes…" />
            <Button
              className="mt-2"
              loading={addFeedback.isPending}
              onClick={() =>
                addFeedback.mutate(
                  { id: demo.id, payload: { rating: Number(fbRating) || 0, status: fbStatus as 'accepted' | 'revision_required' | 'rejected', feedback: fbText.trim() } },
                  {
                    onSuccess: () => {
                      toast.success('Feedback submitted.');
                      setFbText('');
                    },
                    onError: () => toast.error('Failed to submit feedback.'),
                  },
                )
              }
            >
              Submit Feedback
            </Button>
          </div>

          {canDelete && DEMO_DELETE_ENABLED && (
            <div className="border-t border-outline pt-3">
              <Button variant="danger" loading={deleteDemo.isPending} onClick={() => setConfirmDel(true)}>
                Delete Demo
              </Button>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmDel}
        title="Delete demo"
        message={`Delete "${demo?.title ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteDemo.isPending}
        onConfirm={() =>
          demo &&
          deleteDemo.mutate(demo.id, {
            onSuccess: () => {
              toast.success('Demo deleted.');
              setConfirmDel(false);
              onClose();
            },
            onError: () => toast.error('Delete failed.'),
          })
        }
        onCancel={() => setConfirmDel(false)}
      />
    </Drawer>
  );
}
